// This script includes all score cards in one file without importing external components

import { generateClient } from "aws-amplify/api";
import { getCurrentUser } from "aws-amplify/auth";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
const client = generateClient();
// ---------- LAB CONFIG + HELPERS ----------

const labsConfig = {
  // Lipids
  LabsTG: {
    label: "Triglycerides",
    rangeText: "< 150 mg/dL",
    type: "lt",
    max: 150,
  },
  LabsTC: {
    label: "Cholesterol (Total)",
    rangeText: "< 200 mg/dL",
    type: "lt",
    max: 200,
  },
  LabsHDL: {
    label: "Cholesterol (HDL)",
    rangeText: "> 39 mg/dL",
    type: "gt",
    min: 39,
  },
  LabsLDL: {
    label: "Cholesterol (LDL)",
    rangeText: "< 100 mg/dL",
    type: "lt",
    max: 100,
  },

  // Minerals
  LabsNa: {
    label: "Sodium",
    rangeText: "134 – 144 mmol/L",
    type: "between",
    min: 134,
    max: 144,
  },
  LabsK: {
    label: "Potassium",
    rangeText: "3.5 – 5.2 mmol/L",
    type: "between",
    min: 3.5,
    max: 5.2,
  },
  LabsP: {
    label: "Phosphorus",
    rangeText: "2.5 – 4.5 mg/dL",
    type: "between",
    min: 2.5,
    max: 4.5,
  },
  LabsCl: {
    label: "Chloride",
    rangeText: "96 – 106 mmol/L",
    type: "between",
    min: 96,
    max: 106,
  },
  LabsZn: {
    label: "Zinc",
    rangeText: "44 – 115 mcg/dL",
    type: "between",
    min: 44,
    max: 115,
  },
  LabsCa: {
    label: "Calcium",
    rangeText: "8.7 – 10.2 mg/dL",
    type: "between",
    min: 8.7,
    max: 10.2,
  },
  LabsMg: {
    label: "Magnesium",
    rangeText: "1.6 – 2.3 mg/dL",
    type: "between",
    min: 1.6,
    max: 2.3,
  },
  LabsFe: {
    label: "Iron",
    rangeText: "149 – 505 mcg/dL",
    type: "between",
    min: 149,
    max: 505,
  },
  LabsFerritin: {
    label: "Ferritin",
    rangeText: "11 – 336 ng/mL",
    type: "between",
    min: 11,
    max: 336,
  },

  // Vitamins
  LabsVitA: {
    label: "Vitamin A",
    rangeText: "20.1 – 62 mcg/dL",
    type: "between",
    min: 20.1,
    max: 62,
  },
  LabsVitB1: {
    label: "Thiamin (B1)",
    rangeText: "2.5 – 7.5 μg/dL",
    type: "between",
    min: 2.5,
    max: 7.5,
  },
  LabsVitB2: {
    label: "Riboflavin (B2)",
    rangeText: "106 – 638 nmol/L",
    type: "between",
    min: 106,
    max: 638,
  },
  LabsVitB3: {
    label: "Niacin (B3)",
    rangeText: "0.50 – 8.45 μg/mL",
    type: "between",
    min: 0.5,
    max: 8.45,
  },
  LabsVitB5: {
    label: "Pantothenic Acid (B5)",
    rangeText: "1.6 – 2.7 μmol/L",
    type: "between",
    min: 1.6,
    max: 2.7,
  },
  LabsVitB6: {
    label: "Pyridoxine (B6)",
    rangeText: "5 – 50 μg/L",
    type: "between",
    min: 5,
    max: 50,
  },
  LabsVitB7: {
    label: "Biotin (B7)",
    rangeText: "133 – 329 pmol/L",
    type: "between",
    min: 133,
    max: 329,
  },
  LabsVitB9: {
    label: "Folate (B9)",
    rangeText: "> 3 ng/mL",
    type: "gt_or_eq",
    min: 3,
  },
  LabsVitB12: {
    label: "Cobalamin (B12)",
    rangeText: "232 – 1245 pg/mL",
    type: "between",
    min: 232,
    max: 1245,
  },
  LabsVitC: {
    label: "Vitamin C",
    rangeText: "0.4 – 2 mg/L",
    type: "between",
    min: 0.4,
    max: 2,
  },
  LabsVitD: {
    label: "Vitamin D, 25-Hydroxy",
    rangeText: "30.0 – 100.0 ng/mL",
    type: "between",
    min: 30,
    max: 100,
  },
  LabsVitE: {
    label: "Vitamin E (Alpha Tocopherol)",
    rangeText: "7 – 25.1 mg/L",
    type: "between",
    min: 7,
    max: 25.1,
  },
  LabsVitK: {
    label: "Vitamin K",
    rangeText: "0.22 – 4.88 nmol/L",
    type: "between",
    min: 0.22,
    max: 4.88,
  },
};

const lipidKeys = ["LabsTG", "LabsTC", "LabsHDL", "LabsLDL"];

const mineralKeys = [
  "LabsNa",
  "LabsK",
  "LabsP",
  "LabsCl",
  "LabsZn",
  "LabsCa",
  "LabsMg",
  "LabsFe",
  "LabsFerritin",
];

const vitaminKeys = [
  "LabsVitA",
  "LabsVitB1",
  "LabsVitB2",
  "LabsVitB3",
  "LabsVitB5",
  "LabsVitB6",
  "LabsVitB7",
  "LabsVitB9",
  "LabsVitB12",
  "LabsVitC",
  "LabsVitD",
  "LabsVitE",
  "LabsVitK",
];

function isLabValueInRange(fieldKey, rawValue) {
  if (rawValue === null || rawValue === undefined || rawValue === "")
    return null;
  const cfg = labsConfig[fieldKey];
  if (!cfg) return null;

  const value = parseFloat(rawValue);
  if (Number.isNaN(value)) return null;

  const { type, min, max } = cfg;

  switch (type) {
    case "lt":
      return value < max;
    case "gt":
      return value > min;
    case "gt_or_eq":
      return value >= min;
    case "between":
      return value >= min && value <= max;
    default:
      return null;
  }
}

function getLabDotColor(fieldKey, rawValue) {
  const inRange = isLabValueInRange(fieldKey, rawValue);
  if (inRange === null) return "#cccccc"; // no value
  return inRange ? "#28a745" : "#B00000"; // green / red
}

// Hook: latest lab results for this user
function useLatestLabResults() {
  const [latestLabs, setLatestLabs] = useState(null);
  const [loadingLabs, setLoadingLabs] = useState(true);
  const [errorLabs, setErrorLabs] = useState(null);

  useEffect(() => {
    async function fetchLabs() {
      try {
        setLoadingLabs(true);
        const user = await getCurrentUser();
        const uid =
          user?.attributes?.sub || user?.username || user?.userId || user?.id;
        if (!uid) throw new Error("User not authenticated");

        const query = `
          query ListLabResults {
            listLabResults(
              limit: 50,
              filter: { or: [{ owner: { eq: "${uid}" } }, { userId: { eq: "${uid}" } }] }
            ) {
              items {
                id
                CompletedDate
                CompletedTime
                ${[...lipidKeys, ...mineralKeys, ...vitaminKeys].join("\n")}
                createdAt
              }
            }
          }
        `;

        const res = await client.graphql({ query });
        const items = res?.data?.listLabResults?.items || [];

        if (!items.length) {
          setLatestLabs(null);
          return;
        }

        // Sort by CompletedDate desc (fallback to createdAt)
        const sorted = [...items].sort((a, b) => {
          const dA = new Date(a.CompletedDate || a.createdAt || 0);
          const dB = new Date(b.CompletedDate || b.createdAt || 0);
          return dB - dA;
        });

        setLatestLabs(sorted[0]);
      } catch (err) {
        console.error("Error fetching lab results:", err);
        setErrorLabs(err.message || "Unknown error");
        setLatestLabs(null);
      } finally {
        setLoadingLabs(false);
      }
    }

    fetchLabs();
  }, []);

  return { latestLabs, loadingLabs, errorLabs };
}
function LabSummaryCard({
  title,
  domainName,
  fieldKeys,
  latestLabs,
  loading,
  error,
}) {
  // calculate % in range
  let totalFields = fieldKeys.length;
  let inRangeCount = 0;

  if (latestLabs) {
    fieldKeys.forEach((key) => {
      const raw = latestLabs[key];
      const inRange = isLabValueInRange(key, raw);
      if (inRange === true) inRangeCount += 1;
    });
  }

  const completedDate =
    latestLabs?.CompletedDate || latestLabs?.createdAt || null;
  const completedTime = latestLabs?.CompletedTime || null;

  // 🔹 NEW: tabs + actions state (same structure as ScoreCard)
  const [activeTab, setActiveTab] = useState("summary");
  const [actions, setActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(true);
  const [errorActions, setErrorActions] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);

  useEffect(() => {
    async function fetchActions() {
      setLoadingActions(true);
      setErrorActions(null);
      try {
        const user = await getCurrentUser();
        const uid =
          user?.attributes?.sub || user?.username || user?.userId || user?.id;
        if (!uid) throw new Error("User not authenticated");

        const query = `
          query ListTrackPlans {
            listTrackPlans(filter: {
              or: [{ owner: { eq: "${uid}" } }, { userId: { eq: "${uid}" } }],
              Discontinued: { eq: false },
              Successful: { eq: false },
              ActionDomain: { eq: "${domainName}" }
            }) {
              items {
                id
                Action
                Percentage
              }
            }
          }
        `;

        const res = await client.graphql({ query });
        const items = res?.data?.listTrackPlans?.items || [];

        const mapped = items.map(({ id, Action, Percentage }) => ({
          id,
          name: Action,
          completion: Percentage ?? 0,
        }));

        setActions(mapped);
      } catch (err) {
        setErrorActions(err.message || "Unknown error");
        setActions([]);
      } finally {
        setLoadingActions(false);
      }
    }

    fetchActions();
  }, [domainName]);

  return (
    <>
      <div
        style={{
          padding: 24,
          border: "1px solid #ccc",
          borderRadius: 12,
          backgroundColor: "#fff",
          boxShadow: "0 3px 12px rgba(0,0,0,0.1)",
          userSelect: "none",
          position: "relative",
          fontFamily: "Arial, sans-serif",
          marginBottom: 20,
          marginTop: 0,
        }}
      >
        <h2 style={{ textAlign: "center", marginBottom: 12 }}>{title}</h2>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 20,
            marginBottom: 18,
          }}
        >
          {["summary", "actions"].map((key) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                cursor: "pointer",
                padding: "10px 24px",
                borderRadius: 20,
                border:
                  activeTab === key ? "2px solid #0067B3" : "1px solid #ccc",
                backgroundColor: activeTab === key ? "#d9eaff" : "#fff",
                fontWeight: "bold",
                minWidth: 100,
                fontSize: 14,
              }}
            >
              {key === "summary" ? "Summary" : "Actions"}
            </button>
          ))}
        </div>

        {/* SUMMARY TAB */}
        {activeTab === "summary" && (
          <>
            <div style={{ marginBottom: 16, textAlign: "center" }}>
              {loading ? (
                <div style={{ fontSize: 14, color: "#333" }}>
                  Loading latest labs…
                </div>
              ) : error ? (
                <div style={{ fontSize: 14, color: "#B00000" }}>
                  Error loading labs
                </div>
              ) : !latestLabs ? (
                <div style={{ fontSize: 14, color: "#333" }}>
                  No lab results yet.
                </div>
              ) : (
                <>
                  <div
                    style={{
                      fontSize: 13,
                      color: "#333",
                      marginTop: 8,
                    }}
                  >
                    In healthy range ({inRangeCount}/{totalFields})<br />
                    {completedDate && (
                      <span style={{ fontSize: 12 }}>
                        Latest: {completedDate}
                        {completedTime ? ` at ${completedTime}` : ""}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* colored bars for each lab field */}
            {latestLabs && (
              <div style={{ marginTop: 8 }}>
                {fieldKeys.map((key) => {
                  const cfg = labsConfig[key];
                  if (!cfg) return null;
                  const raw = latestLabs[key];
                  const display =
                    raw === null || raw === undefined || raw === ""
                      ? "N/A"
                      : raw.toString();
                  const barColor = getLabDotColor(key, raw);

                  return (
                    <div key={key} style={{ marginBottom: 10 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 4,
                          fontSize: 13,
                          color: "#003366",
                        }}
                      >
                        <span>{cfg.label}</span>
                        <span style={{ fontWeight: "bold" }}>{display}</span>
                      </div>
                      <div
                        style={{
                          height: 8,
                          borderRadius: 999,
                          backgroundColor: "#eee",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: barColor,
                            transition: "background-color 0.3s ease",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#777",
                          marginTop: 2,
                        }}
                      >
                        Ref: {cfg.rangeText}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ACTIONS TAB */}
        {activeTab === "actions" && (
          <div style={{ maxWidth: 480, margin: "0 auto" }}>
            {loadingActions && <div>Loading actions...</div>}
            {errorActions && (
              <div style={{ color: "red" }}>Error: {errorActions}</div>
            )}
            {!loadingActions && actions.length === 0 && (
              <div
                style={{
                  fontStyle: "italic",
                  color: "#777",
                  textAlign: "center",
                }}
              >
                No actions in progress for this lab category.
              </div>
            )}
            {!loadingActions &&
              actions.map(({ id, name, completion }) => (
                <div
                  key={id}
                  onClick={() => setSelectedAction({ id, name, completion })}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 16,
                    padding: 12,
                    border: "1px solid #ccc",
                    borderRadius: 12,
                    backgroundColor: "#f9f9f9",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{ flexGrow: 1, fontWeight: "bold", fontSize: 16 }}
                  >
                    {name}
                  </div>
                  <CircleGauge
                    size={50}
                    strokeWidth={6}
                    percentage={completion}
                    label={`${completion}%`}
                  />
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Modal for action details (reusing pattern from ScoreCard) */}
      {selectedAction &&
        ReactDOM.createPortal(
          <>
            <div
              onClick={() => setSelectedAction(null)}
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                zIndex: 999,
              }}
            />
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1000,
                backgroundColor: "#fff",
                padding: 32,
                borderRadius: 16,
                boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
                width: 400,
                maxWidth: "90vw",
                textAlign: "center",
              }}
            >
              <h3>{selectedAction.name}</h3>
              <CircleGauge
                size={120}
                strokeWidth={10}
                percentage={selectedAction.completion}
                label={`${selectedAction.completion}%`}
              />
              <button
                onClick={() => setSelectedAction(null)}
                style={{
                  backgroundColor: "#0067B3",
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 24px",
                  fontWeight: "bold",
                  fontSize: 16,
                  cursor: "pointer",
                  marginTop: 20,
                }}
              >
                Close
              </button>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

// --- Score fetching hooks ---
// Retained for legacy score-card integrations outside the current dashboard.
// eslint-disable-next-line no-unused-vars
function useFetchMultipleScores(scoreKeys = []) {
  const [score, setScore] = useState(null);
  const [previousScore, setPreviousScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scoreKeysKey = scoreKeys.join(",");

  useEffect(() => {
    async function fetchScores() {
      try {
        setLoading(true);
        const selectedScoreKeys = scoreKeysKey.split(",").filter(Boolean);
        const user = await getCurrentUser();
        const uid =
          user?.attributes?.sub || user?.username || user?.userId || user?.id;
        if (!uid) throw new Error("User not authenticated");

        // nothing to query if no score keys supplied
        if (!selectedScoreKeys.length) {
          setScore(null);
          setPreviousScore(null);
          setLoading(false);
          setError(null);
          return;
        }

        const query = `
          query ListScores {
            listAssessmentScores(limit: 50, filter: {
              or: [{ owner: { eq: "${uid}" } }, { userId: { eq: "${uid}" } }],
              isActive: { eq: false }
            }) {
              items {
                id
                ${scoreKeysKey.replace(/,/g, "\n")}
                updatedAt
              }
            }
          }
        `;

        const res = await client.graphql({ query });
        const items = res?.data?.listAssessmentScores?.items || [];
        const sorted = items.sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
        );

        const calculateTotal = (entry) =>
          selectedScoreKeys.reduce(
            (sum, key) => sum + (Number(entry?.[key]) || 0),
            0,
          );

        if (sorted.length === 0) {
          setScore(null);
          setPreviousScore(null);
        } else if (sorted.length === 1) {
          setScore(calculateTotal(sorted[0]));
          setPreviousScore(null);
        } else {
          setScore(calculateTotal(sorted[0]));
          setPreviousScore(calculateTotal(sorted[1]));
        }
      } catch (err) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    }

    fetchScores();
  }, [scoreKeysKey]);

  return {
    score: score !== null ? Math.round(score) : null,
    previousScore: previousScore !== null ? Math.round(previousScore) : null,
    loading,
    error,
  };
}

function useFetchScore(scoreKey) {
  const [score, setScore] = useState(null);
  const [previousScore, setPreviousScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchScore() {
      try {
        setLoading(true);
        const user = await getCurrentUser();
        const uid =
          user?.attributes?.sub || user?.username || user?.userId || user?.id;
        if (!uid) throw new Error("User not authenticated");

        // if no scoreKey provided, skip building the query
        if (!scoreKey) {
          setScore(null);
          setPreviousScore(null);
          setLoading(false);
          setError(null);
          return;
        }

        const query = `
          query ListScores {
            listAssessmentScores(limit: 50, filter: {
              or: [{ owner: { eq: "${uid}" } }, { userId: { eq: "${uid}" } }],
              isActive: { eq: false }
            }) {
              items {
                id
                ${scoreKey}
                updatedAt
              }
            }
          }
        `;

        const res = await client.graphql({ query });
        const items = res?.data?.listAssessmentScores?.items || [];
        const sorted = items.sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
        );

        if (sorted.length === 0) {
          setScore(null);
          setPreviousScore(null);
        } else if (sorted.length === 1) {
          setScore(sorted[0][scoreKey]);
          setPreviousScore(null);
        } else {
          setScore(sorted[0][scoreKey]);
          setPreviousScore(sorted[1][scoreKey]);
        }
      } catch (err) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    }

    fetchScore();
  }, [scoreKey]);

  return {
    score: score !== null ? Math.round(Number(score)) : null,
    previousScore:
      previousScore !== null ? Math.round(Number(previousScore)) : null,
    loading,
    error,
  };
}

function getBarColorCustom(score, thresholds) {
  if (typeof score !== "number") return "#ccc";
  for (let i = 0; i < thresholds.length; i++) {
    if (score >= thresholds[i].min && score <= thresholds[i].max) {
      return thresholds[i].color;
    }
  }
  return "#ccc";
}

function CircleGauge({ size, strokeWidth, percentage, label }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  // Updated color logic here:
  const getColor = (p) => {
    if (p >= 80) return "#44cc44"; // green
    if (p >= 70) return "#cccc44"; // yellow
    return "#cc4444"; // red
  };

  const strokeColor = getColor(percentage);

  return (
    <svg
      width={size}
      height={size}
      style={{ overflow: "visible", display: "block", margin: "0 auto" }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#eee"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={size / 4}
        fill="#333"
        fontWeight="bold"
        style={{ userSelect: "none" }}
      >
        {label}
      </text>
    </svg>
  );
}

const wholeHealthDimensions = [
  {
    key: "mental",
    label: "Mental",
    scoreKey: "MHScore",
    max: 66,
    thresholds: [0, 16, 32],
    color: "#4d9f70",
  },
  {
    key: "physical",
    label: "Physical",
    scoreKey: "PHScore",
    max: 30,
    thresholds: [0, 5, 10],
    color: "#4b86a8",
  },
  {
    key: "spiritual",
    label: "Spiritual",
    scoreKey: "SHScore",
    max: 28,
    thresholds: [0, 7, 14],
    color: "#b08a38",
  },
  {
    key: "social",
    label: "Social",
    scoreKey: "SoHScore",
    max: 48,
    thresholds: [0, 12, 24],
    color: "#9a6075",
  },
];

const wholeHealthStatusColor = (status) => {
  if (status === "Strong") return "#3b7d23";
  if (status === "Opportunity") return "#d39e00";
  if (status === "Attention Needed") return "#b00000";
  return "#9ca3af";
};

const wholeHealthResponseFields = {
  mental: [
    ["MHDxPTSD", "Diagnosed with PTSD"],
    ["MHDxDepression", "Diagnosed with depression"],
    ["MHSleeplessness", "Experience sleeplessness"],
    ["MHAnxiety", "Experience anxiety"],
    ["MHDepression", "Experience depression"],
    ["MHStress", "Experience stress"],
    ["MHSuicidalThoughts", "Have suicidal thoughts"],
    ["MHLoneliness", "Feel loneliness"],
    ["MHWorry", "Feel worried"],
    ["MHHope", "Feel hopelessness"],
  ],
  physical: [
    ["PHDxInfertility", "Pregnancy or fertility issues"],
    ["PHDxCurableSTD", "Curable STD/STI diagnosis"],
    ["PHDxIncurableSTD", "Incurable STD diagnosis"],
    ["PHDxCancer", "Diagnosed with cancer"],
    ["PHDxDiabetes", "Diagnosed with diabetes"],
    ["PHDxHighBlood", "Diagnosed with high blood pressure"],
    ["PHDxHeartDisease", "Diagnosed with heart disease"],
    ["PHDxIrritableBowel", "Irritable bowel issues"],
    ["PHDxVitA", "Severe vitamin A deficiency"],
    ["PHDxVitB", "Severe vitamin B deficiency"],
    ["PHDxVitC", "Severe vitamin C deficiency"],
    ["PHDxVitD", "Severe vitamin D deficiency"],
    ["PHDxVitE", "Severe vitamin E deficiency"],
    ["PHDxVitK", "Severe vitamin K deficiency"],
    ["PHDxAutoimmune", "Diagnosed with an autoimmune condition"],
    ["PHDxOtherPhysical", "Other serious physical health diagnosis"],
    ["PHVegeFruits", "Eat vegetables and fruits"],
    ["PHBeanLentils", "Eat beans and lentils"],
    ["PHMeat", "Eat meat"],
    ["PHFishSeafood", "Eat fish and seafood"],
    ["PHSweets", "Consume sweets"],
    ["PHWater", "Drink water"],
    ["PHPhysicalActivity", "Engage in physical activity"],
  ],
  spiritual: [
    ["SHSpiritualDefine", "Spirituality defines lifestyle"],
    ["SHSpiritualIntegrate", "Spirituality is integrated into life"],
    ["SHPrayer", "Frequency of prayer"],
    ["SHSpiritualActivity", "Healthy spiritual activities"],
    ["SHReadText", "Reading spiritual or religious text"],
    ["SHCommunity", "Engaging with spiritual community"],
  ],
  social: [
    ["SoHStable", "Financial stability"],
    ["SoHSafety", "Safety and security"],
    ["SoHRelationships", "Healthy relationships"],
    ["SoHBoundaries", "Healthy boundaries"],
    ["SoHEUnpleasant", "Experience unpleasant emotions"],
    ["SoHEControl", "Control over emotions"],
  ],
};

const wholeHealthStatus = (score, thresholds) => {
  if (score === null || score === undefined) return "Not available";
  if (score <= thresholds[1]) return "Strong";
  if (score <= thresholds[2]) return "Opportunity";
  return "Attention Needed";
};

const wholeHealthWeight = (status) =>
  status === "Strong" ? 1 : status === "Opportunity" ? 1.5 : 2;

function WholeHealthDonut({
  dimensions,
  selectedKey,
  onSelect,
  muted = false,
}) {
  const totalWeight = dimensions.reduce(
    (sum, dimension) => sum + wholeHealthWeight(dimension.status),
    0,
  );
  const outerRadius = 132;
  const innerRadius = 78;
  const center = 150;
  const isInteractive = typeof onSelect === "function";
  let accumulated = 0;

  const pointAt = (radius, angle) => ({
    x: center + radius * Math.cos((angle * Math.PI) / 180),
    y: center + radius * Math.sin((angle * Math.PI) / 180),
  });

  const segmentPath = (startAngle, endAngle) => {
    const gap = 7;
    const trimAngle = (radius) => (Math.asin(gap / 2 / radius) * 180) / Math.PI;
    const outerTrim = trimAngle(outerRadius);
    const innerTrim = trimAngle(innerRadius);
    const outerStart = pointAt(outerRadius, startAngle + outerTrim);
    const outerEnd = pointAt(outerRadius, endAngle - outerTrim);
    const innerEnd = pointAt(innerRadius, endAngle - innerTrim);
    const innerStart = pointAt(innerRadius, startAngle + innerTrim);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return [
      `M ${outerStart.x} ${outerStart.y}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
      `L ${innerEnd.x} ${innerEnd.y}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
      "Z",
    ].join(" ");
  };

  return (
    <svg
      className={`whole-health-donut${muted ? " whole-health-donut-muted" : ""}`}
      viewBox="0 0 300 300"
      role="img"
      aria-label="Four sections of the Whole Health Picture. Larger sections represent more attention needed."
    >
      {dimensions.map((dimension) => {
        const weight = wholeHealthWeight(dimension.status);
        const percentage = weight / totalWeight;
        const startAngle = -90 + accumulated * 360;
        accumulated += percentage;
        const endAngle = -90 + accumulated * 360;
        const middleAngle = (startAngle + endAngle) / 2;
        const labelPoint = pointAt(
          (outerRadius + innerRadius) / 2,
          middleAngle,
        );
        const isSelected = selectedKey === dimension.key;
        return (
          <g key={dimension.key}>
            <path
              className={`whole-health-segment${isSelected ? " is-selected" : ""}`}
              d={segmentPath(startAngle, endAngle)}
              fill={wholeHealthStatusColor(dimension.status)}
              style={{ fill: wholeHealthStatusColor(dimension.status) }}
              tabIndex={isInteractive ? "0" : undefined}
              role={isInteractive ? "button" : undefined}
              aria-label={`${dimension.label} health. Score ${dimension.score} out of ${dimension.max}. ${dimension.status}. This section represents ${Math.round(percentage * 100)} percent of the Whole Health Picture.`}
              onClick={() => onSelect?.(dimension.key)}
              onKeyDown={(event) => {
                if (
                  isInteractive &&
                  (event.key === "Enter" || event.key === " ")
                ) {
                  event.preventDefault();
                  onSelect(dimension.key);
                }
              }}
            />
            <text
              className="whole-health-segment-label"
              x={labelPoint.x}
              y={labelPoint.y}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {dimension.label}
            </text>
          </g>
        );
      })}
      <circle
        cx={center}
        cy={center}
        r={innerRadius}
        className="whole-health-donut-hole"
      />
      <text
        x={center}
        y="143"
        textAnchor="middle"
        className="whole-health-center-label"
      >
        WHOLE
      </text>
      <text
        x={center}
        y="163"
        textAnchor="middle"
        className="whole-health-center-label"
      >
        HEALTH
      </text>
    </svg>
  );
}

function useWholeHealthScores() {
  return [
    { ...wholeHealthDimensions[0], ...useFetchScore("MHScore") },
    { ...wholeHealthDimensions[1], ...useFetchScore("PHScore") },
    { ...wholeHealthDimensions[2], ...useFetchScore("SHScore") },
    { ...wholeHealthDimensions[3], ...useFetchScore("SoHScore") },
  ];
}

function useWholeHealthActions(domainName) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchActions() {
      try {
        setLoading(true);
        const user = await getCurrentUser();
        const uid =
          user?.attributes?.sub || user?.username || user?.userId || user?.id;
        if (!uid) throw new Error("User not authenticated");

        const res = await client.graphql({
          query: `
            query ListWholeHealthActions {
              listTrackPlans(filter: {
                or: [{ owner: { eq: "${uid}" } }, { userId: { eq: "${uid}" } }]
              }) {
                items { id Action ActionNote ActionDomain Percentage Discontinued Successful }
              }
            }
          `,
        });
        const expectedDomain = `${domainName} Health`;
        const legacyDomains = domainName === "Social" ? ["Unknown"] : [];
        setActions(
          (res?.data?.listTrackPlans?.items || [])
            .filter(
              (action) =>
                action.Discontinued !== true &&
                action.Successful !== true &&
                (action.ActionDomain === expectedDomain ||
                  legacyDomains.includes(action.ActionDomain)),
            )
            .map((action) => ({
              id: action.id,
              name: action.Action,
              note: action.ActionNote,
              completion: action.Percentage ?? 0,
            })),
        );
      } catch (error) {
        console.error("Unable to load Whole Health actions:", error);
        setActions([]);
      } finally {
        setLoading(false);
      }
    }

    fetchActions();
  }, [domainName]);

  return { actions, loading };
}

function useWholeHealthResponses(dimensionKey, enabled) {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchResponses() {
      if (!enabled) {
        setResponses([]);
        return;
      }
      const fields = wholeHealthResponseFields[dimensionKey] || [];
      const fieldNames = fields.map(([key]) => key);
      try {
        setLoading(true);
        const user = await getCurrentUser();
        const uid =
          user?.attributes?.sub || user?.username || user?.userId || user?.id;
        if (!uid) throw new Error("User not authenticated");
        const [scoresResult, answersResult] = await Promise.all([
          client.graphql({
            query: `query WholeHealthResponseScores { listAssessmentScores(limit: 50, filter: { or: [{ owner: { eq: "${uid}" } }, { userId: { eq: "${uid}" } }], isActive: { eq: false } }) { items { updatedAt ${fieldNames.join(" ")} } } }`,
          }),
          client.graphql({
            query: `query WholeHealthResponses { listAssessmentAnswers(limit: 50, filter: { or: [{ owner: { eq: "${uid}" } }, { userId: { eq: "${uid}" } }], isActive: { eq: false } }) { items { updatedAt ${fieldNames.join(" ")} } } }`,
          }),
        ]);
        const latestScore = [
          ...(scoresResult?.data?.listAssessmentScores?.items || []),
        ].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0];
        const latestAnswers = [
          ...(answersResult?.data?.listAssessmentAnswers?.items || []),
        ].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0];
        setResponses(
          fields
            .filter(([key]) => Number(latestScore?.[key]) >= 2)
            .map(([key, question]) => ({
              question,
              response: latestAnswers?.[key] ?? "No response recorded",
              status:
                Number(latestScore?.[key]) === 2
                  ? "Opportunity"
                  : "Attention Needed",
            })),
        );
      } catch (error) {
        console.error("Unable to load Whole Health responses:", error);
        setResponses([]);
      } finally {
        setLoading(false);
      }
    }
    fetchResponses();
  }, [dimensionKey, enabled]);

  return { responses, loading };
}

function WholeHealthDashboard({ setCurrentView }) {
  const [selectedKey, setSelectedKey] = useState("mental");
  const [showPrevious, setShowPrevious] = useState(false);
  const [showResponses, setShowResponses] = useState(false);
  const scoreResults = useWholeHealthScores();
  const currentDimensions = scoreResults.map((dimension) => ({
    ...dimension,
    score: dimension.score,
    status: wholeHealthStatus(dimension.score, dimension.thresholds),
  }));
  const previousDimensions = scoreResults.map((dimension) => ({
    ...dimension,
    score: dimension.previousScore,
    status: wholeHealthStatus(dimension.previousScore, dimension.thresholds),
  }));
  const selected = (showPrevious ? previousDimensions : currentDimensions).find(
    (dimension) => dimension.key === selectedKey,
  );
  const isLoading = scoreResults.some((dimension) => dimension.loading);
  const hasCompletedAssessment = currentDimensions.some(
    (dimension) => dimension.score !== null && dimension.score !== undefined,
  );
  const { actions, loading: loadingActions } = useWholeHealthActions(
    selected?.label || "Mental",
  );
  const { responses, loading: loadingResponses } = useWholeHealthResponses(
    selectedKey,
    showResponses,
  );

  if (!isLoading && !hasCompletedAssessment) {
    return (
      <section
        className="whole-health-dashboard"
        aria-labelledby="whole-health-title"
      >
        <header className="whole-health-header">
          <h1 id="whole-health-title">Your Whole Health Picture</h1>
          <p>Four Dimensions. One Whole.</p>
        </header>
        <div className="whole-health-empty">
          <p className="whole-health-eyebrow">WHA RESULTS</p>
          <h2>Complete your assessment to see your results.</h2>
          <p>
            Your Whole Health Picture will appear here after you complete the
            assessment.
          </p>
          <button
            type="button"
            className="whole-health-plan-button"
            onClick={() => setCurrentView?.("Assessment")}
          >
            Start Assessment
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className="whole-health-dashboard"
      aria-labelledby="whole-health-title"
    >
      <header className="whole-health-header">
        <h1 id="whole-health-title">Your Whole Health Picture</h1>
        <p>Four Dimensions. One Whole.</p>
      </header>
      <div className="whole-health-main">
        <div className="whole-health-visual-column">
          <WholeHealthDonut
            dimensions={showPrevious ? previousDimensions : currentDimensions}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
          />
          <div
            className="whole-health-legend"
            aria-label="Assessment status legend"
          >
            <span>
              <i className="status-dot status-strong" /> Strong
            </span>
            <span>
              <i className="status-dot status-opportunity" /> Opportunity
            </span>
            <span>
              <i className="status-dot status-attention" /> Attention Needed
            </span>
          </div>
          <p className="whole-health-size-note">
            Larger section = More attention needed
          </p>
        </div>
        <div className="whole-health-detail">
          <p className="whole-health-kicker">
            Strengthen the four dimensions that make up your whole health.
          </p>
          {isLoading ? (
            <p>Loading your assessment...</p>
          ) : (
            selected && (
              <>
                <div className="whole-health-detail-heading">
                  <div>
                    <p className="whole-health-eyebrow">SELECTED DIMENSION</p>
                    <h2>{selected.label}</h2>
                  </div>
                  <span
                    className={`whole-health-status status-${selected.status.toLowerCase().replaceAll(" ", "-")}`}
                  >
                    {selected.status}
                  </span>
                </div>
                <p className="whole-health-score">
                  {selected.score ?? "-"} <span>/ {selected.max}</span>
                </p>
                <button
                  type="button"
                  className="whole-health-link"
                  onClick={() => setShowResponses((value) => !value)}
                  aria-expanded={showResponses}
                >
                  {showResponses ? "Hide responses" : "Explore Your Responses"}{" "}
                  <span
                    className="whole-health-inline-arrow"
                    aria-hidden="true"
                  >
                    &#8594;
                  </span>
                </button>
                {showResponses && (
                  <div className="whole-health-responses">
                    <h4>
                      {selected.status === "Attention Needed"
                        ? "Areas that may need attention"
                        : "Responses to explore"}
                    </h4>
                    {loadingResponses ? (
                      <p>Loading your responses...</p>
                    ) : responses.length ? (
                      <ul>
                        {responses.map((response) => (
                          <li key={response.question}>
                            <i
                              className={`response-status-dot status-${(response.status || "Opportunity").toLowerCase().replaceAll(" ", "-")}`}
                            />
                            <div>
                              <strong>{response.question}</strong>
                              <span>{String(response.response)}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>
                        No scored responses in this dimension need attention
                        right now.
                      </p>
                    )}
                  </div>
                )}
                <div className="whole-health-actions">
                  <div>
                    <p className="whole-health-eyebrow">YOUR ACTIONS</p>
                    {loadingActions ? (
                      <p>Loading actions...</p>
                    ) : actions.length ? (
                      <div className="whole-health-action-list">
                        {actions.map((action) => (
                          <div className="whole-health-action" key={action.id}>
                            <div className="whole-health-action-copy">
                              <span>{action.name}</span>
                              {action.name?.trim().toLowerCase() === "other" &&
                                action.note &&
                                action.note.trim().toLowerCase() !== "n/a" && (
                                  <small>{action.note}</small>
                                )}
                            </div>
                            <CircleGauge
                              size={50}
                              strokeWidth={6}
                              percentage={action.completion}
                              label={`${action.completion}%`}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>No active actions in this dimension yet.</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="whole-health-plan-button"
                    onClick={() => setCurrentView?.("CreatePlan")}
                  >
                    Create Plan
                  </button>
                </div>
              </>
            )
          )}
        </div>
      </div>
      <section
        className="whole-health-progress"
        aria-labelledby="progress-title"
      >
        <div className="whole-health-progress-heading">
          <div>
            <p className="whole-health-eyebrow">YOUR CHANGE OVER TIME</p>
            <h2 id="progress-title">Then and Now</h2>
            <p className="whole-health-progress-summary">
              See how each part of your whole health has shifted since your last
              assessment.
            </p>
          </div>
          <button
            type="button"
            className="whole-health-switch"
            onClick={() => setShowPrevious((value) => !value)}
            aria-pressed={showPrevious}
          >
            {showPrevious
              ? "Current Whole Health Picture"
              : "Previous Whole Health Picture"}
          </button>
        </div>
        <div className="whole-health-comparison">
          <div className="whole-health-picture">
            <span>Previous assessment</span>
            <WholeHealthDonut dimensions={previousDimensions} muted />
          </div>
          <div className="whole-health-arrow" aria-hidden="true">
            &#8594;
          </div>
          <div className="whole-health-picture">
            <span>Current assessment</span>
            <WholeHealthDonut dimensions={currentDimensions} />
          </div>
        </div>
        <div className="whole-health-progress-list">
          {currentDimensions.map((dimension, index) => (
            <div key={dimension.key}>
              <strong>{dimension.label}</strong>
              <span>
                {previousDimensions[index].score ?? "-"}{" "}
                <b className="whole-health-inline-arrow">&#8594;</b>{" "}
                {dimension.score ?? "-"}
              </span>
              <small>
                {previousDimensions[index].status}{" "}
                <b className="whole-health-inline-arrow">&#8594;</b>{" "}
                {dimension.status}
              </small>
            </div>
          ))}
        </div>
      </section>
    </section>
  );
}

// --- ScoreCard component ---
// Retained for legacy score-card integrations outside the current dashboard.
// eslint-disable-next-line no-unused-vars
function ScoreCard({
  domainName,
  scoreKey,
  maxScore,
  thresholds,
  customScoreHook,
  customScoreHookArgs,
  noTabs = false,
  triplePills = false,
  summaryLines = [],
  navigationButtons = [], // ✅ NEW
}) {
  const multipleScores = customScoreHook?.(...(customScoreHookArgs || []));
  const singleScore = useFetchScore(scoreKey);
  const scoreData = customScoreHook ? multipleScores : singleScore;
  const { score, previousScore } = scoreData;

  // NEW: State for track plan actions for this domain
  const [actions, setActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(true);
  const [errorActions, setErrorActions] = useState(null);

  useEffect(() => {
    async function fetchActions() {
      setLoadingActions(true);
      setErrorActions(null);
      try {
        const user = await getCurrentUser();
        const uid =
          user?.attributes?.sub || user?.username || user?.userId || user?.id;
        if (!uid) throw new Error("User not authenticated");

        const query = `
          query ListTrackPlans {
            listTrackPlans(filter: {
              or: [{ owner: { eq: "${uid}" } }, { userId: { eq: "${uid}" } }],
              Discontinued: { eq: false },
              Successful: { eq: false },
              ActionDomain: { eq: "${domainName}" }
            }) {
              items {
                id
                Action
                Percentage
              }
            }
          }
        `;

        const res = await client.graphql({ query });
        const items = res?.data?.listTrackPlans?.items || [];

        if (items.length === 0) {
          setActions([]);
        } else {
          // Map items to shape used in rendering below
          const mapped = items.map(({ id, Action, Percentage }) => ({
            id,
            name: Action,
            completion: Percentage ?? 0,
          }));
          setActions(mapped);
        }
      } catch (err) {
        setErrorActions(err.message || "Unknown error");
        setActions([]);
      } finally {
        setLoadingActions(false);
      }
    }

    fetchActions();
  }, [domainName]);

  const percent = score !== null ? (score / maxScore) * 100 : 100;
  const [markerLeft, setMarkerLeft] = useState("100%");

  useEffect(() => {
    const timeout = setTimeout(() => {
      setMarkerLeft(`calc(${percent}% - 4px)`);
    }, 50);
    return () => clearTimeout(timeout);
  }, [percent]);

  const [activeTab, setActiveTab] = useState("summary");
  const [selectedAction, setSelectedAction] = useState(null);

  return (
    <>
      <div
        style={{
          padding: 24,
          border: "1px solid #ccc",
          borderRadius: 12,
          backgroundColor: "#fff",
          boxShadow: "0 3px 12px rgba(0,0,0,0.1)",
          userSelect: "none",
          position: "relative",
          fontFamily: "Arial, sans-serif",
          marginBottom: 20,
          marginTop: 0,
        }}
      >
        {navigationButtons && navigationButtons.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center", // center horizontally
              marginBottom: 24,
              gap: 12,
            }}
          >
            {navigationButtons.map((btn, i) => (
              <button
                key={i}
                onClick={btn.disabled ? undefined : btn.onClick}
                disabled={btn.disabled}
                style={{
                  padding: "10px 24px",
                  fontSize: 16,
                  fontWeight: "bold",
                  color: btn.disabled ? "#888" : "#0067B3",
                  border: `2px solid ${btn.disabled ? "#888" : "#0067B3"}`,
                  borderRadius: 12,
                  backgroundColor: btn.disabled ? "#f0f0f0" : "white",
                  cursor: btn.disabled ? "not-allowed" : "pointer",
                  transition: "background-color 0.3s, color 0.3s",
                  minWidth: 180,
                  userSelect: "none",
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>
        )}

        <h2 style={{ textAlign: "center", marginBottom: 20 }}>{domainName}</h2>

        <div
          style={{
            height: 28,
            width: "100%",
            borderRadius: 16,
            backgroundColor: getBarColorCustom(score, thresholds),
            overflow: "hidden",
            marginBottom: 8,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -4,
              left: markerLeft,
              width: 8,
              height: 36,
              backgroundColor: "#fff",
              borderRadius: 4,
              animation: "pulseGlow 1s ease-out infinite",
              pointerEvents: "none",
              transition: "left 0.8s ease-out",
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 16,
            marginBottom: 24,
          }}
        >
          <span style={{ fontWeight: "bold", color: "#0067B3" }}>0</span>
          <span style={{ fontWeight: "bold", color: "#0067B3" }}>
            {maxScore}
          </span>
        </div>

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: "bold", color: "#0067B3" }}>
            Score: {score ?? "No score yet"}
          </div>
          <div
            style={{ fontSize: 16, color: "#333", marginTop: 4, minHeight: 50 }}
          >
            Previous Score: {previousScore ?? "No previous score yet"}
          </div>
        </div>

        {!noTabs && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 20,
              marginBottom: 22,
            }}
          >
            {["summary", "actions"].map((key) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                style={{
                  cursor: "pointer",
                  padding: "10px 24px",
                  borderRadius: 20,
                  border:
                    activeTab === key ? "2px solid #0067B3" : "1px solid #ccc",
                  backgroundColor: activeTab === key ? "#d9eaff" : "#fff",
                  fontWeight: "bold",
                  minWidth: 100,
                  fontSize: 16,
                }}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            ))}
          </div>
        )}

        {(activeTab === "summary" || noTabs) && (
          <>
            {summaryLines.length > 0 &&
              (triplePills ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    marginTop: 8,
                    alignItems: "center",
                  }}
                >
                  {summaryLines.slice(0, 3).map(({ label, value }, i) => (
                    <div
                      key={i}
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span style={{ fontSize: 16 }}>{label}</span>
                      <span
                        style={{
                          backgroundColor: "#0067B3",
                          color: "white",
                          borderRadius: 10,
                          padding: "2px 16px",
                          fontWeight: "bold",
                          fontSize: 10,
                        }}
                      >
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: "center", fontSize: 16 }}>
                  {summaryLines.map(({ label, value }, i) => (
                    <span key={i} style={{ marginRight: 12 }}>
                      {label}
                      <span
                        style={{
                          backgroundColor: "#0067B3",
                          color: "white",
                          borderRadius: 20,
                          padding: "6px 16px",
                          fontWeight: "bold",
                          fontSize: 16,
                          marginLeft: 6,
                        }}
                      >
                        {value}
                      </span>
                    </span>
                  ))}
                </div>
              ))}
          </>
        )}

        {!noTabs && activeTab === "actions" && (
          <div style={{ maxWidth: 480, margin: "0 auto" }}>
            {loadingActions && <div>Loading actions...</div>}
            {errorActions && (
              <div style={{ color: "red" }}>Error: {errorActions}</div>
            )}
            {!loadingActions && actions.length === 0 && (
              <div
                style={{
                  fontStyle: "italic",
                  color: "#777",
                  textAlign: "center",
                }}
              >
                No actions in progress for this domain.
              </div>
            )}
            {!loadingActions &&
              actions.map(({ id, name, completion }) => (
                <div
                  key={id}
                  onClick={() => setSelectedAction({ id, name, completion })}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 16,
                    padding: 12,
                    border: "1px solid #ccc",
                    borderRadius: 12,
                    backgroundColor: "#f9f9f9",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{ flexGrow: 1, fontWeight: "bold", fontSize: 16 }}
                  >
                    {name}
                  </div>
                  <CircleGauge
                    size={50}
                    strokeWidth={6}
                    percentage={completion}
                    label={`${completion}%`}
                  />
                </div>
              ))}
          </div>
        )}
      </div>

      {selectedAction &&
        ReactDOM.createPortal(
          <>
            <div
              onClick={() => setSelectedAction(null)}
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                zIndex: 999,
              }}
            />
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1000,
                backgroundColor: "#fff",
                padding: 32,
                borderRadius: 16,
                boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
                width: 400,
                maxWidth: "90vw",
                textAlign: "center",
              }}
            >
              <h3>{selectedAction.name}</h3>
              <CircleGauge
                size={120}
                strokeWidth={10}
                percentage={selectedAction.completion}
                label={`${selectedAction.completion}%`}
              />
              <button
                onClick={() => setSelectedAction(null)}
                style={{
                  backgroundColor: "#0067B3",
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 24px",
                  fontWeight: "bold",
                  fontSize: 16,
                  cursor: "pointer",
                  marginTop: 20,
                }}
              >
                Close
              </button>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

// --- All Score Cards Grid ---
export default function AllScoreCards({ setCurrentView, sidebarOpen }) {
  const [columns, setColumns] = useState(1);
  const [resultsView, setResultsView] = useState("wha");
  const { latestLabs, loadingLabs, errorLabs } = useLatestLabResults();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width >= 1200) setColumns(3);
      else if (width >= 768) setColumns(2);
      else setColumns(1);
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  const [visibleCards, setVisibleCards] = useState(() => {
    const saved = localStorage.getItem("visibleCards");
    return saved
      ? JSON.parse(saved)
      : {
          coping: true,
          mental: true,
          physical: true,
          spiritual: true,
          social: true,
          overall: true,
        };
  });

  const toggleCardVisibility = (key) => {
    setVisibleCards((prev) => {
      const updated = {
        ...prev,
        [key]: !prev[key],
      };
      localStorage.setItem("visibleCards", JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <div
      className={`individual-dashboard${sidebarOpen ? " sidebar-open" : ""}`}
      style={{
        width: isMobile
          ? "calc(100vw - 32px)"
          : sidebarOpen
            ? "calc(100vw - 328px)"
            : "calc(100vw - 48px)",
        maxWidth: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <nav
        className="whole-health-nav dashboard-results-nav"
        aria-label="Results navigation"
      >
        <button
          type="button"
          className={resultsView === "wha" ? "is-active" : ""}
          aria-current={resultsView === "wha" ? "page" : undefined}
          onClick={() => setResultsView("wha")}
        >
          WHA RESULTS
        </button>
        <button
          type="button"
          className={resultsView === "labs" ? "is-active" : ""}
          aria-current={resultsView === "labs" ? "page" : undefined}
          onClick={() => setResultsView("labs")}
        >
          LAB RESULTS
        </button>
      </nav>
      {/* Card Visibility (spans grid width) */}
      <div
        className="legacy-dashboard-controls is-hidden"
        style={{
          display: "none",
          gridTemplateColumns: `repeat(${columns}, minmax(320px, 1fr))`,
          gap: 24,
          width: "100%",
          maxWidth: 1200,
          margin: "0 auto 16px",
          padding: "0 16px",
          boxSizing: "border-box",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            gridColumn: "1 / -1",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              marginTop: 0,
              fontSize: "16px",
              fontWeight: "600",
              color: "#0067B3",
              marginBottom: "12px",
              textAlign: "center",
              letterSpacing: "0.5px",
            }}
          >
            Card Visibility
          </div>

          <div
            className="toggle-bar"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: isMobile ? "wrap" : "nowrap", // changes based on screen size
              overflowX: isMobile ? "auto" : "visible",
              padding: "12px 24px",
              gap: "12px",

              background: "#f5faff",
              borderBottom: "2px solid #d0e4f2",
              borderRadius: "12px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.05)",

              width: "100%",
              maxWidth: "100%",
              whiteSpace: isMobile ? "normal" : "nowrap",
            }}
          >
            {[
              ["Coping", "coping"],
              ["Mental", "mental"],
              ["Physical", "physical"],
              ["Spiritual", "spiritual"],
              ["Social", "social"],
              ["Overall", "overall"],
            ].map(([label, key]) => (
              <button
                key={key}
                onClick={() => toggleCardVisibility(key)}
                style={{
                  cursor: "pointer",
                  padding: "10px 24px",
                  borderRadius: 20,
                  border: visibleCards[key]
                    ? "2px solid #0067B3"
                    : "1px solid #ccc",
                  backgroundColor: visibleCards[key] ? "#d9eaff" : "#fff",
                  fontWeight: "bold",
                  minWidth: 120,
                  fontSize: 14,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <style>
        {`
  @media (max-width: 368px) {
    .toggle-bar {
      flex-wrap: wrap; /* allow wrapping only on small screens */
      white-space: normal;
    }
  }
`}
      </style>

      {/* Legend (under Card Visibility, spans grid width) */}
      <div
        className="legacy-dashboard-controls is-hidden"
        style={{
          display: "none",
          gridTemplateColumns: `repeat(${columns}, minmax(320px, 1fr))`,
          gap: 24,
          width: "100%",
          maxWidth: 1200,
          margin: "0 auto 16px",
          padding: "0 16px",
          boxSizing: "border-box",
          justifyContent: "center",
        }}
      >
        <div style={{ gridColumn: "1 / -1" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 12,
              padding: "12px 14px",
              borderRadius: 12,
              border: "1px solid #d0e4f2",
              background: "#f5faff",
              boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
            }}
          >
            {[
              {
                color: "#28a745",
                label: "Green – Within a Healthy Range",
              },
              {
                color: "#ffc107",
                label: "Yellow – Could Use More Care",
              },
              {
                color: "#B00000",
                label: "Red – Outside a Healthy Range",
              },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 12px",
                  borderRadius: 999,
                  backgroundColor: "white",
                  border: "1px solid #e6eef5",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    backgroundColor: item.color,
                    boxShadow: "0 0 0 3px rgba(0,0,0,0.04)",
                    flex: "0 0 auto",
                  }}
                />
                <span
                  style={{ fontSize: 13, color: "#003366", fontWeight: 600 }}
                >
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {resultsView === "wha" && (
        <WholeHealthDashboard setCurrentView={setCurrentView} />
      )}
      {/* 🔹 SECTION 2: Lab Results Overview */}
      {resultsView === "labs" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, minmax(320px, 1fr))`,
            gap: 24,
            width: "100%",
            maxWidth: 1200,
            margin: "32px auto 0",
            padding: "0 16px",
            boxSizing: "border-box",
            justifyContent: "center",
          }}
        >
          <div style={{ gridColumn: "1 / -1", textAlign: "center" }}>
            <h2
              style={{
                fontSize: 20,
                fontWeight: "700",
                color: "#003366",
                marginBottom: 8,
                textAlign: "center",
              }}
            >
              Lab Results Overview
            </h2>
            <p
              style={{
                fontSize: 13,
                color: "#333",
                marginBottom: 12,
                maxWidth: "100%",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              These cards summarize the latest lab results you've inputted and
              show if values are in a healthy range based on general
              expectations.
            </p>
            <div
              style={{
                height: 2,
                width: "100%",
                background:
                  "linear-gradient(to right, #0067B3, rgba(77,166,255,0.1))",
                borderRadius: 999,
              }}
            />
          </div>
          <LabSummaryCard
            title="Lipids"
            domainName="Lipids"
            fieldKeys={lipidKeys}
            latestLabs={latestLabs}
            loading={loadingLabs}
            error={errorLabs}
          />

          <LabSummaryCard
            title="Minerals"
            domainName="Minerals"
            fieldKeys={mineralKeys}
            latestLabs={latestLabs}
            loading={loadingLabs}
            error={errorLabs}
          />

          <LabSummaryCard
            title="Vitamins"
            domainName="Vitamins"
            fieldKeys={vitaminKeys}
            latestLabs={latestLabs}
            loading={loadingLabs}
            error={errorLabs}
          />
        </div>
      )}
    </div>
  );
}
